<script setup lang="ts">
import { UiDialog, UiText, UiTextarea } from '@shopexperts/shared';
import { ref, watch, computed } from 'vue';
import { useRespondToReview } from '../../composables/reviews/useRespondToReview';

const open = defineModel<boolean>('open', { required: true });

const props = defineProps<{
  reviewId: number;
  existingResponse?: string;
}>();

const responseText = ref('');
const error = ref('');
const { respondToReview, updateResponse, isSubmitting } = useRespondToReview();

// Initialize with existing response if editing
watch(() => props.existingResponse, (value) => {
  if (value) {
    responseText.value = value;
  }
}, { immediate: true });

const validate = () => {
  if (!responseText.value.trim()) {
    error.value = 'Response cannot be empty';
    return false;
  }
  if (responseText.value.trim().length < 10) {
    error.value = 'Response must be at least 10 characters';
    return false;
  }
  error.value = '';
  return true;
};

const handleSubmit = async () => {
  if (!validate()) return;

  try {
    if (props.existingResponse) {
      await updateResponse(props.reviewId, responseText.value.trim());
    } else {
      await respondToReview(props.reviewId, responseText.value.trim());
    }
    open.value = false;
    responseText.value = '';
    error.value = '';
  } catch (err) {
    // Error handling is done in the composable
  }
};

const handleClose = () => {
  responseText.value = props.existingResponse || '';
  error.value = '';
};

const primaryLabel = computed(() => 
  props.existingResponse ? 'Update Response' : 'Submit Response'
);
</script>

<template>
  <UiDialog 
    v-model="open"
    v-model:loading="isSubmitting"
    :title="existingResponse ? 'Edit Your Response' : 'Write Your Response'"
    size="lg"
    :primary-action-label="primaryLabel"
    secondary-action-label="Cancel"
    @primary-action="handleSubmit"
    @secondary-action="open = false"
    @close="handleClose"
  >
    <div class="space-y-4">
      <div>
        <UiText as="label" variant="p-small" class="block font-medium text-gray-700 mb-2" text="Your Response" />
        <UiTextarea
          v-model="responseText"
          :rows="6"
          placeholder="Write your response to this review..."
          class="w-full"
          @input="error = ''"
        />
        <UiText v-if="error" as="p" variant="p-small" class="mt-2 text-red-600" :text="error" />
        <UiText as="p" variant="p-small" class="mt-2 text-gray-500" text="Minimum 10 characters" />
      </div>
    </div>
  </UiDialog>
</template>

